
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ArrowLeft, LockKeyhole } from 'lucide-react';
import Logo from '@/components/Logo';
import { resetPassword } from '@/contexts/auth/services/passwordService';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Extract token from URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const resetToken = queryParams.get('token');
    
    if (!resetToken) {
      toast({
        title: "Hiányzó vagy érvénytelen token",
        description: "A jelszó visszaállításához érvényes tokenre van szükség.",
        variant: "destructive",
      });
      navigate('/login');
      return;
    }
    
    setToken(resetToken);
  }, [location, navigate, toast]);

  const validateForm = () => {
    if (password.length < 6) {
      toast({
        title: "Gyenge jelszó",
        description: "A jelszónak legalább 6 karakter hosszúnak kell lennie.",
        variant: "destructive",
      });
      return false;
    }
    
    if (password !== confirmPassword) {
      toast({
        title: "A jelszavak nem egyeznek",
        description: "A megadott jelszavak nem egyeznek meg.",
        variant: "destructive",
      });
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !token) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await resetPassword(token, password);
      
      if (result.success) {
        setIsComplete(true);
        toast({
          title: "Jelszó sikeresen frissítve",
          description: "Bejelentkezhet az új jelszavával.",
        });
      } else {
        toast({
          title: "Hiba történt",
          description: result.message || "Nem sikerült frissíteni a jelszót. Kérjük, próbálja újra.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast({
        title: "Hiba történt",
        description: "Nem sikerült frissíteni a jelszót. Kérjük, próbálja újra.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto flex items-center justify-center min-h-screen p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-6">
            <Logo className="h-12" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            {isComplete ? "Jelszó frissítve" : "Új jelszó beállítása"}
          </CardTitle>
          <CardDescription className="text-center">
            {isComplete 
              ? "A jelszava sikeresen frissítve lett" 
              : "Adjon meg egy új jelszót a fiókjához"}
          </CardDescription>
        </CardHeader>
        
        {!isComplete ? (
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Új jelszó</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Legalább 6 karakter"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Jelszó megerősítése</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Ismételje meg a jelszót"
                  required
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Feldolgozás...
                  </>
                ) : (
                  "Jelszó frissítése"
                )}
              </Button>
            </CardFooter>
          </form>
        ) : (
          <CardContent className="space-y-6">
            <div className="flex justify-center my-6">
              <LockKeyhole className="h-16 w-16 text-medihub-primary" />
            </div>
            
            <div className="text-center space-y-4">
              <p>
                Jelszava sikeresen megváltozott.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full"
              >
                Bejelentkezés
              </Button>
            </div>
          </CardContent>
        )}
        
        <CardFooter className="pt-0">
          <div className="w-full text-center text-sm">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="inline-flex items-center text-medihub-primary hover:underline"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Vissza a bejelentkezéshez
            </button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ResetPassword;
